import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { fetchClient } from '@src/api/actions/clients';
import { listAssignees } from '@src/api/tasks';
import { listOnboardingTasks, updateOnboardingTask } from '@src/api/onboardingWorkflow';
import { useMessage } from '@src/context/common';
import paths from '@src/paths';

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'complete', label: 'Complete' },
];

const STATUS_META = {
  not_started: { color: 'default', icon: HourglassTopIcon },
  in_progress: { color: 'info', icon: PlayArrowIcon },
  blocked: { color: 'warning', icon: BlockIcon },
  complete: { color: 'success', icon: CheckCircleIcon },
};

const formatOwner = (owner) => (owner === 'client' ? 'Client' : 'Guru');

const buildEvidenceValue = (value, type) => {
  if (type === 'boolean') {
    return Boolean(value);
  }
  if (type === 'date') {
    return value || '';
  }
  return value || '';
};

function aggregateBlockers(rootTask, children) {
  const blockers = [];
  const pushBlockers = (task) => {
    (task.blocked_by || []).forEach((blocker) => {
      blockers.push({
        key: `${task.template_key}-${blocker.key}`,
        title: blocker.title,
        owner: blocker.owner,
      });
    });
  };
  pushBlockers(rootTask);
  children.forEach(pushBlockers);
  return blockers;
}

function getAggregateStatus(rootTask, children) {
  if (!children.length) {
    return rootTask.status;
  }
  const hasClientBlocker = children.some((child) => child.blocked_by_client);
  if (hasClientBlocker) {
    return 'blocked';
  }
  if (children.some((child) => child.status === 'blocked')) {
    return 'blocked';
  }
  if (children.some((child) => child.status === 'in_progress')) {
    return 'in_progress';
  }
  if (children.every((child) => child.status === 'complete')) {
    return 'complete';
  }
  return 'not_started';
}

function RootTaskCard({
  rootTask,
  children,
  onSelect,
}) {
  const aggregateStatus = getAggregateStatus(rootTask, children);
  const meta = STATUS_META[aggregateStatus] || STATUS_META.not_started;
  const Icon = meta.icon;
  const blockers = aggregateBlockers(rootTask, children);
  const completedCount = children.filter((child) => child.status === 'complete').length;
  const totalCount = children.length;
  const pendingCount = Math.max(totalCount - completedCount, 0);

  return (
    <Card
      variant="outlined"
      sx={{
        borderLeft: 4,
        borderLeftColor: `${meta.color}.main`,
        background: aggregateStatus === 'blocked' ? 'rgba(255, 152, 0, 0.04)' : 'transparent',
      }}
    >
      <CardContent sx={{ py: 1.25 }}>
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Icon fontSize="small" color={meta.color} />
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {rootTask.template_title}
            </Typography>
            <Chip size="small" label={formatOwner(rootTask.owner)} />
            <Chip size="small" color={meta.color} label={STATUS_OPTIONS.find((o) => o.value === aggregateStatus)?.label || 'Ready'} />
          </Stack>
          {children.length ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {completedCount}/{totalCount} complete
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.round((completedCount / totalCount) * 100)}
                sx={{ flex: 1, height: 6, borderRadius: 999 }}
              />
              <Chip size="small" label={`${pendingCount} pending`} />
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No subtasks
            </Typography>
          )}
          {blockers.length ? (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {blockers.slice(0, 2).map((blocker) => (
                <Chip
                  key={blocker.key}
                  size="small"
                  color={blocker.owner === 'client' ? 'warning' : 'default'}
                  label={`Depends on: ${blocker.title}`}
                  sx={{ mb: 0.5 }}
                />
              ))}
              {blockers.length > 2 ? (
                <Chip size="small" label={`+${blockers.length - 2} more`} />
              ) : null}
            </Stack>
          ) : null}
          {rootTask.soft_note ? (
            <Typography variant="caption" color="text.secondary">
              {rootTask.soft_note}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" variant="text" onClick={() => onSelect(rootTask)}>
              Details
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function OnboardingWorkflowPage() {
  const { clientSlug } = useParams();
  const navigate = useNavigate();
  const message = useMessage();

  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [editState, setEditState] = useState({
    status: '',
    assignee: '',
    notes: '',
    evidence: {},
  });

  const loadTasks = useCallback(async (clientId) => {
    setLoading(true);
    try {
      const resp = await listOnboardingTasks({ client: clientId });
      setTasks(resp?.data?.results || []);
    } catch (error) {
      message.error('Could not load onboarding workflow.');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    if (!clientSlug) return;
    fetchClient(clientSlug)
      .then((resp) => {
        setClient(resp?.data || null);
        if (resp?.data?.id) {
          loadTasks(resp.data.id);
        }
      })
      .catch(() => {
        message.error('Could not load client.');
      });
  }, [clientSlug, loadTasks, message]);

  useEffect(() => {
    listAssignees()
      .then((resp) => setAssignees(resp?.data?.results || resp?.data || []))
      .catch(() => setAssignees([]));
  }, []);

  const tasksByPhase = useMemo(() => {
    const rootTasks = tasks.filter((task) => !task.parent_template_key);
    return rootTasks.reduce((acc, task) => {
      const phase = task.phase || 'Uncategorized';
      acc[phase] = acc[phase] || [];
      acc[phase].push(task);
      return acc;
    }, {});
  }, [tasks]);

  const childrenByParent = useMemo(() => {
    return tasks.reduce((acc, task) => {
      if (!task.parent_template_key) return acc;
      acc[task.parent_template_key] = acc[task.parent_template_key] || [];
      acc[task.parent_template_key].push(task);
      return acc;
    }, {});
  }, [tasks]);

  const orderedChildrenByParent = useMemo(() => {
    return Object.fromEntries(
      Object.entries(childrenByParent).map(([key, children]) => [
        key,
        [...children].sort((a, b) => (a.template_sort_order || 0) - (b.template_sort_order || 0)),
      ])
    );
  }, [childrenByParent]);

  const rootTaskMeta = useMemo(() => {
    return tasks
      .filter((task) => !task.parent_template_key)
      .map((task) => ({
        task,
        children: orderedChildrenByParent[task.template_key] || [],
      }));
  }, [tasks, orderedChildrenByParent]);

  const phaseLanes = useMemo(() => {
    const lanes = {};
    rootTaskMeta.forEach(({ task, children }) => {
      const phase = task.phase || 'Uncategorized';
      lanes[phase] = lanes[phase] || [];
      lanes[phase].push({ task, children });
    });
    Object.keys(lanes).forEach((phase) => {
      lanes[phase].sort((a, b) => (a.task.template_sort_order || 0) - (b.task.template_sort_order || 0));
    });
    return lanes;
  }, [rootTaskMeta]);

  const leafTasks = useMemo(() => {
    const parentKeys = new Set(tasks.map((task) => task.parent_template_key).filter(Boolean));
    return tasks.filter((task) => !parentKeys.has(task.template_key));
  }, [tasks]);

  const progress = useMemo(() => {
    if (!leafTasks.length) return { total: 0, complete: 0, percent: 0 };
    const complete = leafTasks.filter((task) => task.status === 'complete').length;
    return { total: leafTasks.length, complete, percent: Math.round((complete / leafTasks.length) * 100) };
  }, [leafTasks]);

  const clientProgress = useMemo(() => {
    const clientTasks = leafTasks.filter((task) => task.owner === 'client');
    if (!clientTasks.length) return { total: 0, complete: 0, percent: 0 };
    const complete = clientTasks.filter((task) => task.status === 'complete').length;
    return { total: clientTasks.length, complete, percent: Math.round((complete / clientTasks.length) * 100) };
  }, [leafTasks]);

  const guruProgress = useMemo(() => {
    const guruTasks = leafTasks.filter((task) => task.owner !== 'client');
    if (!guruTasks.length) return { total: 0, complete: 0, percent: 0 };
    const complete = guruTasks.filter((task) => task.status === 'complete').length;
    return { total: guruTasks.length, complete, percent: Math.round((complete / guruTasks.length) * 100) };
  }, [leafTasks]);

  const blockedByClientCount = useMemo(
    () => tasks.filter((task) => task.blocked_by_client).length,
    [tasks]
  );

  const handleSelectTask = (task) => {
    setActiveTask(task);
    setEditState({
      status: task.status,
      assignee: task.assignee || '',
      notes: task.notes || '',
      evidence: { ...(task.evidence || {}) },
    });
    setDrawerOpen(true);
  };

  const handleEvidenceChange = (key, value) => {
    setEditState((prev) => ({
      ...prev,
      evidence: { ...prev.evidence, [key]: value },
    }));
  };

  const handleSave = async () => {
    if (!activeTask) return;
    try {
      await updateOnboardingTask(activeTask.id, {
        status: editState.status,
        assignee: editState.assignee || null,
        notes: editState.notes,
        evidence: editState.evidence,
      });
      message.success('Onboarding task updated.');
      setDrawerOpen(false);
      if (client?.id) {
        loadTasks(client.id);
      }
    } catch (error) {
      message.error('Could not update onboarding task.');
    }
  };

  if (!client) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography>Loading client...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4">Onboarding Workflow</Typography>
            <Typography color="text.secondary">{client.name}</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={<Switch checked={showCompleted} onChange={(event) => setShowCompleted(event.target.checked)} />}
              label="Show completed"
            />
            <Button variant="outlined" onClick={() => navigate(paths.onboarding.list)}>
              Back to onboarding list
            </Button>
          </Stack>
        </Stack>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">Overall Progress</Typography>
                  <LinearProgress variant="determinate" value={progress.percent} sx={{ height: 8, borderRadius: 999 }} />
                  <Typography variant="caption" color="text.secondary">
                    {progress.complete}/{progress.total} tasks complete
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">Client-owned Progress</Typography>
                  <LinearProgress variant="determinate" value={clientProgress.percent} color="warning" sx={{ height: 8, borderRadius: 999 }} />
                  <Typography variant="caption" color="text.secondary">
                    {clientProgress.complete}/{clientProgress.total} tasks complete
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">Guru-owned Progress</Typography>
                  <LinearProgress variant="determinate" value={guruProgress.percent} color="success" sx={{ height: 8, borderRadius: 999 }} />
                  <Typography variant="caption" color="text.secondary">
                    {guruProgress.complete}/{guruProgress.total} tasks complete
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip color="warning" label={`Blocked by client: ${blockedByClientCount}`} />
                <Chip label={`Total tasks: ${tasks.length}`} />
                <Chip label={`Phases: ${Object.keys(tasksByPhase).length}`} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {loading && <LinearProgress />}
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
          {Object.entries(phaseLanes).map(([phase, items]) => {
            const laneItems = showCompleted ? items : items.filter(({ task, children }) => {
              const status = getAggregateStatus(task, children);
              return status !== 'complete';
            });
            return (
              <Card
                key={phase}
                variant="outlined"
                sx={{
                  minWidth: 300,
                  maxWidth: 340,
                  flex: '0 0 320px',
                  background: 'rgba(0,0,0,0.01)',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack spacing={0.25}>
                      <Typography variant="h6">{phase}</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip size="small" label={`${laneItems.length} items`} />
                        <Chip size="small" color="warning" label={`${laneItems.filter(({ task, children }) => {
                          const status = getAggregateStatus(task, children);
                          const hasClientBlock = children.some((child) => child.blocked_by_client);
                          return status === 'blocked' && hasClientBlock;
                        }).length} client blockers`} />
                      </Stack>
                    </Stack>
                    <Stack spacing={1}>
                      {laneItems.map(({ task, children }) => (
                        <RootTaskCard
                          key={task.id}
                          rootTask={task}
                          children={children}
                          onSelect={handleSelectTask}
                        />
                      ))}
                      {!laneItems.length ? (
                        <Typography variant="caption" color="text.secondary">
                          No tasks in this phase.
                        </Typography>
                      ) : null}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Stack>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: { xs: 320, sm: 420 }, p: 3 }}>
          {activeTask ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">{activeTask.template_title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {activeTask.phase} · {formatOwner(activeTask.owner)}
                </Typography>
              </Box>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2">Status</Typography>
                <Select
                  size="small"
                  value={editState.status}
                  onChange={(event) => setEditState((prev) => ({ ...prev, status: event.target.value }))}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Assignee</Typography>
                <Select
                  size="small"
                  value={editState.assignee || ''}
                  onChange={(event) => setEditState((prev) => ({ ...prev, assignee: event.target.value }))}
                  displayEmpty
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {assignees.map((assignee) => (
                    <MenuItem key={assignee.id} value={assignee.id}>
                      {assignee.full_name || assignee.email}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              {activeTask.blocked_by?.length ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Blockers</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {activeTask.blocked_by.map((blocker) => (
                      <Chip
                        key={`${activeTask.id}-${blocker.key}`}
                        size="small"
                        color={blocker.owner === 'client' ? 'warning' : 'default'}
                        label={`${blocker.title}`}
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Stack>
                </Stack>
              ) : null}
              {activeTask.required_inputs?.length ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Required Inputs / Evidence</Typography>
                  {activeTask.required_inputs.map((input) => {
                    const value = buildEvidenceValue(editState.evidence?.[input.key], input.type);
                    if (input.type === 'boolean') {
                      return (
                        <FormControlLabel
                          key={input.key}
                          control={
                            <Switch
                              checked={Boolean(value)}
                              onChange={(event) => handleEvidenceChange(input.key, event.target.checked)}
                            />
                          }
                          label={input.label}
                        />
                      );
                    }
                    return (
                      <TextField
                        key={input.key}
                        label={input.label}
                        size="small"
                        type={input.type === 'date' ? 'date' : 'text'}
                        value={value}
                        onChange={(event) => handleEvidenceChange(input.key, event.target.value)}
                        InputLabelProps={input.type === 'date' ? { shrink: true } : undefined}
                      />
                    );
                  })}
                </Stack>
              ) : null}
              <TextField
                label="Notes"
                multiline
                minRows={3}
                value={editState.notes}
                onChange={(event) => setEditState((prev) => ({ ...prev, notes: event.target.value }))}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setDrawerOpen(false)}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSave}>
                  Save
                </Button>
              </Stack>
            </Stack>
          ) : null}
        </Box>
      </Drawer>
    </Box>
  );
}

export default OnboardingWorkflowPage;
